import React, {
  useEffect, useState, useRef, Fragment,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import * as sshSecurityInfoSelectors from 'data/sshSecurityInfo/sshSecurityInfo.selectors';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as sshSecurityInfoActions from 'data/sshSecurityInfo/sshSecurityInfo.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { SshSecurityInfo } from 'data/sshSecurityInfo/sshSecurityInfo.types';
import SshSecurityInformationForm from 'routes/SshSecurityInformation/components/SshSecurityInformationForm';
import { sshSecurityInfoTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
  adaptSshSecurityInfoTemplate,
} from '../../utils/sshSecurityInformationTemplate.adapter';
import { getAuditDetailsToSubmit } from '../../utils/sshSecurityInformationTemplateDataToSubmit';

type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => SshSecurityInfo
}

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  sshSecurityInfoTemplateDef: adaptTemplateVisibilityMode(sshSecurityInfoTemplate, 'edit'),
  deliveryFunctionId: state.global.selectedDF,
  selectedSshSecurityInfo: sshSecurityInfoSelectors
    .getSelectedSshSecurityInfos(state)[0],
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  originalSelectedSshSecurityInfo: sshSecurityInfoSelectors
    .getSelectedSshSecurityInfos(state)[0],
  sshSecurityInfo: sshSecurityInfoSelectors.getSshSecurityInfo(state),
});

const mapDispatch = {
  getSshSecurityInfo: sshSecurityInfoActions.getSshSecurityInfo,
  goToSshSecurityInfoList: sshSecurityInfoActions.goToSshSecurityInfoList,
  updateSshSecurityInfo: sshSecurityInfoActions.updateSshSecurityInfo,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const SshSecurityInformationEdit = ({
  history,
  selectedSshSecurityInfo,
  originalSelectedSshSecurityInfo,
  deliveryFunctionId,
  userId,
  userName,
  isAuditEnabled,
  sshSecurityInfoTemplateDef,
  sshSecurityInfo,
  goToSshSecurityInfoList,
  updateSshSecurityInfo,
  getSshSecurityInfo,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<SshSecurityInfo>>({});
  const [template, setTemplate] = useState(sshSecurityInfoTemplateDef);
  const { t } = useTranslation();

  const id = selectedSshSecurityInfo?.id;

  useEffect(() => {
    if (id) {
      getSshSecurityInfo(deliveryFunctionId, id);
    }
  }, [deliveryFunctionId, id, getSshSecurityInfo]);

  useEffect(() => {
    if (sshSecurityInfo == null || sshSecurityInfoTemplateDef == null) {
      return;
    }

    const clonedSshSecurityInfo = cloneDeep(sshSecurityInfo);
    const clonedTemplate = cloneDeep(sshSecurityInfoTemplateDef);
    clonedTemplate.formId = Date.now().toString();

    setTemplate(clonedTemplate);
    setFormData(clonedSshSecurityInfo);
  }, [sshSecurityInfo, sshSecurityInfoTemplateDef]);

  const getCreateDistributorActionPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const dialedSshSecurityInfoData = formRef.current.getValues();
    const payload = {
      data: dialedSshSecurityInfoData,
      auditDetails: getAuditDetailsToSubmit(
        dialedSshSecurityInfoData,
        originalSelectedSshSecurityInfo,
        isAuditEnabled,
        userId,
        userName,
        'UPDATE',
      ),
    };

    return payload;

  };

  const handleClickSave = () => {
    const payload = getCreateDistributorActionPayload();
    if (payload && sshSecurityInfo != null) {
      updateSshSecurityInfo(deliveryFunctionId, sshSecurityInfo?.id, payload, history);
    }
  };

  const handleCancel = () => goToSshSecurityInfoList(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null || formData == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as SshSecurityInfo);
    let clonedTemplate = cloneDeep(template);

    const newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    clonedTemplate = adaptSshSecurityInfoTemplate(
      clonedTemplate,
      fieldName,
      value,
      newData,
      'edit',
    );

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <SshSecurityInformationForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle:string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (sshSecurityInfo == null) {
    return <Fragment />;
  }

  return (
    <div data-testid="SshSecurityInformationEdit">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('sshSecurityInfo.edit.title'))}
          {renderForm()}
        </FormLayoutContent>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(SshSecurityInformationEdit));
